<template>
  <div>
    <Layout ref="layoutRef" :selectedOption="5" />

    <div class="faq-page">
      <!-- Header -->
      <div class="faq-header">
        <div class="faq-header-icon">
          <span class="material-symbols-outlined">help</span>
        </div>
        <div>
          <h2 class="faq-titulo">Preguntas Frecuentes</h2>
          <p class="faq-subtitulo">Encuentra respuestas rápidas sobre el uso del sistema</p>
        </div>
      </div>

      <!-- Buscador -->
      <div class="faq-search">
        <span class="material-symbols-outlined faq-search-icon">search</span>
        <input
          type="text"
          v-model="busqueda"
          placeholder="Buscar en las preguntas..."
          class="faq-search-input"
        >
        <button v-if="busqueda" class="faq-search-clear" @click="busqueda = ''">
          <span class="material-symbols-outlined" style="font-size:16px">close</span>
        </button>
      </div>

      <!-- Sin resultados -->
      <div v-if="busqueda && !gruposFiltrados.length" class="faq-empty">
        <span class="material-symbols-outlined" style="font-size:40px;color:#9aa0ac">search_off</span>
        <p>No se encontraron resultados para <strong>"{{ busqueda }}"</strong></p>
      </div>

      <!-- Grupos de preguntas -->
      <div v-for="grupo in gruposFiltrados" :key="grupo.titulo" class="faq-grupo">
        <div class="faq-grupo-header">
          <span class="material-symbols-outlined faq-grupo-icono">{{ grupo.icono }}</span>
          <h3 class="faq-grupo-titulo">{{ grupo.titulo }}</h3>
          <span class="faq-grupo-count">{{ grupo.preguntas.length }}</span>
        </div>

        <div class="faq-items">
          <div
            v-for="(item, idx) in grupo.preguntas"
            :key="idx"
            class="faq-item"
            :class="{ 'faq-item--open': item.abierto }"
          >
            <button class="faq-pregunta" @click="toggleItem(item)">
              <span class="faq-pregunta-texto">{{ item.pregunta }}</span>
              <span class="material-symbols-outlined faq-chevron">
                {{ item.abierto ? 'expand_less' : 'expand_more' }}
              </span>
            </button>
            <div class="faq-respuesta" v-show="item.abierto">
              <p v-html="item.respuesta"></p>
            </div>
          </div>
        </div>
      </div>

    </div>

    <Footer />
  </div>
</template>

<script>
import Layout from '@/components/Layout.vue'
import Footer from '@/components/Footer.vue'

export default {
  name: 'FAQ',
  components: { Layout, Footer },

  data() {
    return {
      busqueda: '',
      grupos: [
        {
          titulo: 'Acceso y sesión',
          icono:  'lock',
          preguntas: [
            {
              pregunta: '¿Cómo ingreso al sistema?',
              respuesta: 'Ingresa con tu correo electrónico y contraseña en la pantalla de inicio. Si es tu primer acceso, el administrador del sistema debe haberte creado un usuario previamente.',
              abierto: false,
            },
            {
              pregunta: '¿Qué hago si olvidé mi contraseña?',
              respuesta: 'Contacta al administrador del sistema para que restablezca tu contraseña. Por seguridad, el restablecimiento solo puede hacerlo un usuario con perfil de administrador.',
              abierto: false,
            },
            {
              pregunta: '¿Por qué me cierra la sesión automáticamente?',
              respuesta: 'El sistema cierra la sesión automáticamente tras un período de inactividad por seguridad, especialmente al manejar datos clínicos de pacientes. Guarda tu trabajo antes de alejarte del equipo.',
              abierto: false,
            },
          ],
        },
        {
          titulo: 'Citas',
          icono:  'calendar_month',
          preguntas: [
            {
              pregunta: '¿Cómo registro una nueva cita?',
              respuesta: 'Ve al módulo de <strong>Registros Médicos → Citas</strong>, haz clic en "Nuevo" y completa los datos del paciente, médico, especialidad, fecha y hora.',
              abierto: false,
            },
            {
              pregunta: '¿Cómo cancelo o reprogramo una cita?',
              respuesta: 'Busca la cita en el listado, haz clic en editar y cambia el estado a <strong>CANCELADA</strong> o modifica la fecha y hora según corresponda.',
              abierto: false,
            },
            {
              pregunta: '¿Qué estados puede tener una cita?',
              respuesta: 'Una cita puede estar en los estados: <strong>PROGRAMADA</strong> (agendada y pendiente), <strong>ATENDIDA</strong> (el paciente fue atendido), <strong>CANCELADA</strong> (se canceló antes de la atención) o <strong>NO ASISTIÓ</strong> (el paciente no se presentó).',
              abierto: false,
            },
          ],
        },
        {
          titulo: 'Atenciones',
          icono:  'monitor_heart',
          preguntas: [
            {
              pregunta: '¿Cómo registro una atención médica?',
              respuesta: 'Desde el módulo de <strong>Registros Médicos → Atenciones</strong>, selecciona la cita correspondiente y registra el motivo de consulta, diagnóstico y tratamiento.',
              abierto: false,
            },
            {
              pregunta: '¿Qué diferencia hay entre una atención inicial y una subsecuente?',
              respuesta: '<strong>Inicial</strong>: primera consulta del paciente por un problema de salud. <strong>Subsecuente</strong>: consulta de seguimiento por el mismo problema. Esta clasificación afecta el tipo de registro y, en algunos casos, el tarifario aplicable.',
              abierto: false,
            },
            {
              pregunta: '¿Cómo genero la factura desde una atención?',
              respuesta: 'Una vez registrada la atención, accede al módulo de <strong>Financiero → Facturas</strong>, crea una nueva factura y asocia los servicios prestados desde el tarifario.',
              abierto: false,
            },
            {
              pregunta: '¿Por qué no puedo editar una atención ya facturada?',
              respuesta: 'Las atenciones con facturas emitidas quedan bloqueadas para edición a fin de mantener la integridad del registro clínico y financiero. Si necesitas corregir un dato, contacta al administrador.',
              abierto: false,
            },
          ],
        },
        {
          titulo: 'Facturación y pagos',
          icono:  'receipt_long',
          preguntas: [
            {
              pregunta: '¿Cómo emito una factura?',
              respuesta: 'Ve a <strong>Financiero → Facturas</strong>, crea una nueva factura en estado <strong>PENDIENTE</strong>, agrega los servicios en el detalle y haz clic en <strong>Emitir</strong> para cambiar el estado a <strong>EMITIDA</strong>.',
              abierto: false,
            },
            {
              pregunta: '¿Qué significa cada estado de factura?',
              respuesta: '<strong>PENDIENTE</strong>: creada pero no emitida aún, se puede editar. <strong>EMITIDA</strong>: lista para cobro, no se puede editar el detalle. <strong>PARCIAL</strong>: tiene al menos un pago pero aún queda saldo. <strong>PAGADA</strong>: saldo cubierto en su totalidad. <strong>ANULADA</strong>: cancelada con motivo registrado.',
              abierto: false,
            },
            {
              pregunta: '¿Cómo registro un pago parcial?',
              respuesta: 'En la factura emitida, ve a la pestaña <strong>Pagos</strong> y haz clic en <strong>Registrar Pago</strong>. Ingresa el monto que deseas abonar (menor al saldo total). El sistema actualizará automáticamente el estado a <strong>PARCIAL</strong>.',
              abierto: false,
            },
            {
              pregunta: '¿Cómo anulo una factura?',
              respuesta: 'En la vista de la factura, haz clic en el botón <strong>Anular</strong> e ingresa el motivo de anulación. Esta acción es irreversible.',
              abierto: false,
            },
            {
              pregunta: '¿Por qué no puedo registrar un pago si la factura está en PENDIENTE?',
              respuesta: 'Solo se pueden registrar pagos en facturas con estado <strong>EMITIDA</strong>. Primero debes emitir la factura haciendo clic en el botón "Emitir".',
              abierto: false,
            },
            {
              pregunta: '¿Cómo se actualiza automáticamente el estado de la atención a PAGADA?',
              respuesta: 'El sistema calcula automáticamente el total pagado al registrar cada pago. Cuando el total pagado cubre el valor de la factura, el estado de la atención se actualiza a <strong>PAGADA</strong> sin intervención manual.',
              abierto: false,
            },
          ],
        },
        {
          titulo: 'Tarifario',
          icono:  'price_check',
          preguntas: [
            {
              pregunta: '¿Cómo agrego un nuevo elemento al tarifario?',
              respuesta: 'Ve a <strong>Financiero → Elementos y Tarifario</strong>, haz clic en "Nuevo", busca el nombre estándar del servicio, define el nombre, valor, impuesto y fecha de vigencia.',
              abierto: false,
            },
            {
              pregunta: '¿Cómo actualizo el valor de un servicio?',
              respuesta: 'Busca el elemento en el tarifario y haz clic en editar. Modifica el valor y la fecha de vigencia. Se recomienda registrar la fecha desde la que aplica el nuevo precio.',
              abierto: false,
            },
            {
              pregunta: '¿Qué es el nombre estándar y para qué sirve?',
              respuesta: 'El nombre estándar es el nombre oficial del servicio según el clasificador (CPT, TPSNS, ATC, etc.). Permite estandarizar los servicios independientemente del nombre comercial que use cada empresa.',
              abierto: false,
            },
          ],
        },
        {
          titulo: 'Dashboards y reportes',
          icono:  'leaderboard',
          preguntas: [
            {
              pregunta: '¿Qué período puedo consultar en los reportes?',
              respuesta: 'Puedes consultar cualquier rango de fechas usando los botones de acceso rápido (Hoy, Semana, Mes, Trimestre, Semestre, Año) o ingresando manualmente las fechas de inicio y fin.',
              abierto: false,
            },
            {
              pregunta: '¿Puedo exportar los reportes?',
              respuesta: 'Sí. La mayoría de los reportes tiene botones de exportación a <strong>Excel</strong> y <strong>CSV</strong>. Algunos reportes también permiten exportar a <strong>PDF</strong>.',
              abierto: false,
            },
            {
              pregunta: '¿Con qué frecuencia se actualizan los datos?',
              respuesta: 'Los datos se actualizan en tiempo real. Cada vez que consultas un reporte, el sistema obtiene la información más reciente de la base de datos.',
              abierto: false,
            },
          ],
        },
        {
          titulo: 'Pacientes',
          icono:  'person',
          preguntas: [
            {
              pregunta: '¿Cómo busco un paciente existente?',
              respuesta: 'En cualquier formulario que requiera seleccionar un paciente, escribe al menos 2 caracteres del nombre o número de cédula en el campo de búsqueda. El sistema mostrará las coincidencias automáticamente.',
              abierto: false,
            },
            {
              pregunta: '¿Qué pasa si el paciente no tiene cédula ecuatoriana?',
              respuesta: 'Puedes registrar el paciente con su pasaporte, carné de refugiado u otro documento de identidad. En caso de no tener ningún documento, el sistema puede generar un código temporal de 17 dígitos.',
              abierto: false,
            },
          ],
        },
      ],
    }
  },

  computed: {
    gruposFiltrados() {
      if (!this.busqueda.trim()) return this.grupos
      const q = this.busqueda.toLowerCase().trim()
      return this.grupos
        .map(grupo => ({
          ...grupo,
          preguntas: grupo.preguntas.filter(
            p => p.pregunta.toLowerCase().includes(q) ||
                 p.respuesta.toLowerCase().includes(q)
          ),
        }))
        .filter(grupo => grupo.preguntas.length > 0)
    },
  },

  mounted() {
    this.$refs.layoutRef.toggleProcesos()
    this.$refs.layoutRef.toggleReportes()
    this.$refs.layoutRef.toggleParametros()
    this.$refs.layoutRef.toggleAyuda()
  },

  methods: {
    toggleItem(item) {
      item.abierto = !item.abierto
    },
  },
}
</script>

<style scoped>
.faq-page {
  max-width: 820px;
  margin: 0 auto;
  padding: 1.5rem 1rem 3rem;
}

/* Header */
.faq-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 1.5rem;
}
.faq-header-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #185FA5;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.faq-header-icon .material-symbols-outlined {
  font-size: 26px;
  color: #E6F1FB;
}
.faq-titulo {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 2px;
}
.faq-subtitulo {
  font-size: 0.875rem;
  color: #6c757d;
  margin: 0;
}

/* Buscador */
.faq-search {
  position: relative;
  margin-bottom: 2rem;
}
.faq-search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
  color: #9aa0ac;
  pointer-events: none;
}
.faq-search-input {
  width: 100%;
  height: 42px;
  padding: 0 36px 0 40px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 14px;
  color: #3a3a3a;
  background: #f8f9fa;
  transition: border-color .2s, box-shadow .2s;
}
.faq-search-input:focus {
  outline: none;
  border-color: #185FA5;
  box-shadow: 0 0 0 3px rgba(24,95,165,.12);
  background: #fff;
}
.faq-search-clear {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #9aa0ac;
  display: flex;
  align-items: center;
  padding: 2px;
}
.faq-search-clear:hover { color: #3a3a3a; }

/* Sin resultados */
.faq-empty {
  text-align: center;
  padding: 3rem 1rem;
  color: #6c757d;
}

/* Grupos */
.faq-grupo {
  margin-bottom: 2rem;
}
.faq-grupo-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: .75rem;
  padding-bottom: .5rem;
  border-bottom: 2px solid #e8e8e8;
}
.faq-grupo-icono {
  font-size: 18px;
  color: #185FA5;
}
.faq-grupo-titulo {
  font-size: .95rem;
  font-weight: 600;
  color: #185FA5;
  margin: 0;
  flex: 1;
}
.faq-grupo-count {
  font-size: 11px;
  background: #E6F1FB;
  color: #185FA5;
  border-radius: 10px;
  padding: 1px 7px;
  font-weight: 600;
}

/* Items */
.faq-items {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.faq-item {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
  transition: border-color .2s;
}
.faq-item--open {
  border-color: #185FA5;
}
.faq-pregunta {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: #f8f9fa;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background .15s;
}
.faq-item--open .faq-pregunta {
  background: #E6F1FB;
}
.faq-pregunta:hover { background: #e9ecef; }
.faq-item--open .faq-pregunta:hover { background: #d4e8f8; }
.faq-pregunta-texto {
  font-size: .875rem;
  font-weight: 500;
  color: #212529;
  line-height: 1.4;
}
.faq-item--open .faq-pregunta-texto { color: #0C447C; }
.faq-chevron {
  font-size: 20px;
  color: #9aa0ac;
  flex-shrink: 0;
  transition: transform .2s;
}
.faq-item--open .faq-chevron { color: #185FA5; }
.faq-respuesta {
  padding: 12px 14px;
  background: #fff;
  border-top: 1px solid #e8e8e8;
}
.faq-respuesta p {
  font-size: .875rem;
  color: #495057;
  line-height: 1.6;
  margin: 0;
}

@media (max-width: 576px) {
  .faq-page { padding: 1rem .5rem 2rem; }
  .faq-titulo { font-size: 1.1rem; }
}
</style>